/* eslint-disable @typescript-eslint/no-explicit-any */
import { TOptimizelyMeta } from './OptimizelyMeta';
import { TOptimizelyFeatureExperimentationInstance, getFeatureExperimentationInstance } from './OptimizelyFeatureExperimentationInstance';
import eventBus from 'widgets/toolbox/eventBus';
import { debounce } from 'widgets/toolbox/debounce';

const NAVIGATION_INTERACTIONS = ['swipe', 'arrow_click', 'scrollbar_click'];
const SCROLL_DEPTH_THRESHOLD = 0.75;
const SCROLL_DEBOUNCE_MS = 50;
const FUNNEL_CTA_SELECTOR = 'a, button, [data-track-cta]';
const TRACK_FAILED_MSG = 'Failed to track Optimizely event.';

/**
 * @description Base OptimizelyFeatureExpMeta implementation
 * @param OptimizelyMeta Base widget for extending
 * @returns OptimizelyFeatureExpMeta class
 */
function OptimizelyFeatureExpMetaClassCreator(OptimizelyMeta: TOptimizelyMeta) {
    /**
     * @category widgets
     * @subcategory global
     * @class OptimizelyFeatureExpMeta
     * @augments OptimizelyMeta
     * @property {string} data-widget - Widget name `OptimizelyFeatureExpMeta`
     * @example <caption>Typical OptimizelyFeatureExpMeta widget markup</caption>
     *
     * <meta name="featureExperimentationInstanceData" content="${JSON.stringify(pdict.featureExperimentationInstanceData)}" data-widget="OptimizelyFeatureExpMeta" data-ref="featureExperimentationInstance"/>
     */

    class OptimizelyFeatureExpMeta extends OptimizelyMeta {
        optimizely?: TOptimizelyFeatureExperimentationInstance;

        user?: any;

        loadingPromise?: Promise<TOptimizelyFeatureExperimentationInstance>;

        isTrackingAllowed?: boolean;

        orderTotalValue?: 0;

        isConsentGiven = false;

        lastGalleryImageIndex = 0;

        hasInteractedWithFunnel = false;

        scrollDepthTracked = false;

        exitRateTracked = false;

        scrollDisposable?: Array<() => void>;

        analyticsExpType: string = 'feature_exp';

        OPTIMIZELY_DECISION_INFO: string = 'optimizely_decision_info';

        /**
         * Maps readable constants to the expected feature experimentation event labels.
         */
        EVENTS = {
            ...this.EVENTS,
            ADD_TO_CART: 'addToCart',
            PURCHASES: 'purchases',
            GALLERY_INTERACTION: 'pdp_gallery_interaction',
            IMAGE_NAVIGATION: 'pdp_image_navigation',
            SCROLL_RATE_75: 'scroll_rate_75',
            EXIT_RATE: 'pdp_exit_rate',
            PAGE_LOAD_PERFORMANCE: 'page_load_performance'
        };

        /**
         * Also maps readable constants to the events,
         * but with an additional layer mapping the local eventBus
         * events being triggered to their event labels.
         */
        STATIC_EVENTS = {
            CHECKOUT_FIRST_PAGE: { 'cart.page.submitted': 'checkout_page_1_reach' },
            PROCEED_TO_CHECKOUT: 'proceed_to_checkout_click',
            SIGN_IN_TO_BUY: { 'signInToBuyCheckout.click': 'sign_in_and_register_click' },
            SIGN_IN_TO_BUY_REGISTRATION: { 'signintobuyregistration.submit': 'membership_registration' },
            SIZE_SELECTED: { 'sizeVariation.select': 'shoe_size_selector_click' },
            SIZE_TAB_CLICKED: { 'size.tab.clicked': 'size_selector_click' },
            MOBILE_PLP_IMAGE_SWIPE: { 'plp.image.swipe': 'mobile_plp_image_swipe' },
            PRODUCT_CAROUSEL_SELECTION: { 'product.carousel.selection': 'product_carousel_selection' },
            THUMBNAIL_SELECTOR_CLICK: { 'product.color.change': 'thumbnail_selector_click' }
        };

        init() {
            super.init();

            window.addEventListener('optimizely_decision_info', this.trackAnalyticsEvent.bind(this));
        }

        async initInstance() {
            if (this.initialized) {
                return;
            }

            const featureExperimentationInstanceDataJSON = this.ref('featureExperimentationInstance').attr('content');

            if (!featureExperimentationInstanceDataJSON) {
                return;
            }

            if (!window.optimizelySdk) {
                // eslint-disable-next-line no-console
                console.error('Optimizely SDK is not available on the window object.');

                return;
            }

            const featureExperimentationInstanceData = JSON.parse(featureExperimentationInstanceDataJSON);

            this.isTrackingAllowed = featureExperimentationInstanceData.isTrackingAllowed;

            this.orderTotalValue = featureExperimentationInstanceData.orderTotalValue;

            this.optimizely = getFeatureExperimentationInstance();

            this.registerEvents();

            this.initialized = true;
        }

        /**
         * Registers Optimizely tracking event listeners.
         */
        registerEvents() {
            if (window.contexts.includes('orderconfirmation')) {
                this.orderSubmitted();
            }

            if (window.contexts.includes('pdp')) {
                this.trackEvent(this.EVENTS.PDP);
                this.registerPdpExperimentEvents();
            }

            if (window.contexts.includes('checkout') && document.querySelector('.b-checkout_step-header.m-active')) {
                this.trackEvent(this.EVENTS.CHECKOUT_FIRST_PAGE);
            }

            Object.values(this.STATIC_EVENTS).forEach(ev => {
                const [[eventBusEvent, eventTracked]] = Object.entries(ev);

                eventBus.on(eventBusEvent, () => {
                    this.trackEvent(eventTracked);
                });
            });
        }

        /**
         * Registers the PDP experiment metrics: gallery engagement plus the page-level guardrails.
         */
        registerPdpExperimentEvents(): void {
            eventBus.on('pdp.gallery.interaction', data => this.trackGalleryInteraction(data));

            this.trackScrollDepth();
            this.trackExitRate();
            this.trackPageLoadPerformance();
        }

        /**
         * Forwards gallery interactions to Optimizely.
         *
         * @param data - payload emitted by CoreProductImagesCarousel
         */
        trackGalleryInteraction(data: {
            gallery_type: string;
            gallery_interaction_type: string;
            gallery_image_index: number;
            gallery_images_total: number;
        }): void {
            const interaction = data?.gallery_interaction_type;

            if (!interaction) {
                return;
            }

            const productId = window.analyticsPageData?.id;
            const imageIndex = data.gallery_image_index;

            if (NAVIGATION_INTERACTIONS.indexOf(interaction) === -1) {
                this.lastGalleryImageIndex = imageIndex;

                this.trackEvent(this.EVENTS.GALLERY_INTERACTION, {
                    $opt_event_properties: {
                        product_id: productId,
                        action: interaction
                    }
                }).catch(() => this.logWarn(TRACK_FAILED_MSG));

                return;
            }

            let direction = 'swipe';

            if (interaction !== 'swipe') {
                direction = imageIndex < this.lastGalleryImageIndex ? 'prev' : 'next';
            }

            this.lastGalleryImageIndex = imageIndex;

            this.trackEvent(this.EVENTS.IMAGE_NAVIGATION, {
                $opt_event_properties: {
                    product_id: productId,
                    image_index: imageIndex,
                    direction: direction
                }
            }).catch(() => this.logWarn(TRACK_FAILED_MSG));
        }

        /**
         * Reports once when the visitor reaches 75% scroll depth on the product page.
         */
        trackScrollDepth(): void {
            this.scrollDisposable = this.ev(
                'scroll',
                debounce(() => {
                    if (this.scrollDepthTracked) {
                        return;
                    }

                    const pageHeight = document.documentElement.scrollHeight || document.body.scrollHeight;

                    if (!pageHeight) {
                        return;
                    }

                    if ((window.scrollY + window.innerHeight) / pageHeight >= SCROLL_DEPTH_THRESHOLD) {
                        this.scrollDepthTracked = true;
                        this.disposeScrollListener();
                        this.trackEvent(this.EVENTS.SCROLL_RATE_75).catch(() => this.logWarn(TRACK_FAILED_MSG));
                    }
                }, SCROLL_DEBOUNCE_MS),
                window,
                true
            );
        }

        /**
         * Removes the scroll listener once the threshold has been reported.
         */
        disposeScrollListener(): void {
            if (!this.scrollDisposable) {
                return;
            }

            this.scrollDisposable.forEach(dispose => dispose());
            this.scrollDisposable = undefined;
        }

        /**
         * Reports when the visitor leaves the product page without engaging with the funnel.
         */
        trackExitRate(): void {
            this.ev(
                'click',
                (element: HTMLElement, event: MouseEvent) => {
                    const target = event.target as HTMLElement | null;

                    if (target?.closest?.(FUNNEL_CTA_SELECTOR)) {
                        this.hasInteractedWithFunnel = true;
                    }
                },
                document
            );

            eventBus.on('product.added.to.cart', () => {
                this.hasInteractedWithFunnel = true;
            });

            this.ev(
                'visibilitychange',
                () => {
                    if (document.visibilityState === 'hidden') {
                        this.reportExitRate();
                    }
                },
                document
            );

            this.ev('pagehide', () => this.reportExitRate(), window);
        }

        /**
         * Sends the exit event at most once, and only when the funnel was never engaged.
         */
        reportExitRate(): void {
            if (this.exitRateTracked || this.hasInteractedWithFunnel) {
                return;
            }

            this.exitRateTracked = true;
            this.trackEvent(this.EVENTS.EXIT_RATE).catch(() => this.logWarn(TRACK_FAILED_MSG));
        }

        /**
         * Reports page load duration in milliseconds via Optimizely's reserved numeric field.
         */
        trackPageLoadPerformance(): void {
            const report = () => {
                const [navEntry] = performance.getEntriesByType('navigation') as Array<PerformanceNavigationTiming>;

                if (!navEntry || !navEntry.loadEventEnd) {
                    return;
                }

                this.trackEvent(this.EVENTS.PAGE_LOAD_PERFORMANCE, {
                    value: Math.round(navEntry.loadEventEnd - navEntry.startTime)
                }).catch(() => this.logWarn(TRACK_FAILED_MSG));
            };

            if (document.readyState === 'complete') {
                report();

                return;
            }

            this.ev('load', report, window);
        }

        /**
         * Tracks a custom event using Optimizely if tracking is allowed.
         *
         * @param eventLabel - Name of the event to be tracked
         * @param data - Additional tracking data
         */
        async trackEvent(eventLabel: string, data?): Promise<void> {
            if (!this.isTrackingAllowed || !this.isConsentGiven) {
                return;
            }

            if (this.optimizely) {
                this.optimizely.trackEvent(eventLabel, data);
            } else {
                // eslint-disable-next-line no-console
                this.logWarn('Failed to initialize optimizely sdk.');
            }
        }

        /**
         * Tracks a 'purchase' event.
         *
         */
        orderSubmitted(): void {
            if (!this.orderTotalValue) {
                return;
            }

            /* Revenue tag must not be sent as decimal. */
            const data = {
                revenue: (this.orderTotalValue || 0) * 100
            };

            this.trackEvent(this.EVENTS.PURCHASES, data);
        }
    }

    return OptimizelyFeatureExpMeta;
}

export type TOptimizelyFeatureExpMeta = ReturnType<typeof OptimizelyFeatureExpMetaClassCreator>;

export type TOptimizelyFeatureExpMetaInstance = InstanceType<TOptimizelyFeatureExpMeta>;

export default OptimizelyFeatureExpMetaClassCreator;
